import { all, call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import * as actions from '../actions/actionConstants';
import {
  userLoginSuccess,
  userLogout,
  updateUserProfileSuccess,
  updateUserProfileError,
  updatePasswordSuccess,
  updatePasswordError,
  userRoleUpdateSuccess,
  userRoleUpdateError,
  userInfoUpdateSuccess,
  userInfoUpdateError,
  changeUserSeasonSuccess
} from '../actions/userActions';
import {
  getGamesSuccess,
  getGamesError,
  getUpdatedGamesSuccess,
  getMyGamesSuccess,
  getMyGamesError,
  getUpdatedMyGamesSuccess,
  getGamesByDateSuccess,
  getGamesByDateError,
  filterGamesSuccess,
  filterGamesError,
  getDailySummarySuccess,
  getDailySummaryError,
  updateUmpAvailableForGameSuccess,
  updateUmpAvailableForGameError
} from '../actions/gamesActions';
import {
  getUmpInfoSuccess,
  getUmpInfoError,
  getUmpiresListError,
  getUmpiresListSuccess,
  assignUmpToGameSuccess,
  assignUmpToGameError,
  removeUmpSuccess,
  removeUmpError,
  updateUmpSuccess,
  updateUmpError,
  updateMentorsMenteesSuccess,
  updateMentorsMenteesError,
  getMentorMenteeInfoSuccess,
  getMentorMenteeInfoError,
  getUmpsNextGameSuccess,
  getUmpsNextGameError
} from '../actions/umpireActions';
import { getOrganizationsError, getOrganizationsSuccess } from '../actions/organizationActions';
import {
  getDivisionsError,
  getDivisionsSuccess,
  getSeasonDivisionsError,
  getSeasonDivisionsSuccess
} from '../actions/divisionsActions';
import { 
  getSeasonsError, 
  getSeasonsSuccess,
  getAllSeasonsSuccess,
  updateSeasonMaxDateSuccess,
  updateSeasonMaxDateError,
  updateSeasonActiveSuccess,
  updateSeasonActiveError,
  updateSeasonDisplaySuccess,
  updateSeasonDisplayError
} from '../actions/seasonActions';
import {
  getTeamsError,
  getTeamsSuccess,
  getTeamsVolunteeringSuccess,
  getTeamsVolunteeringError,
  updateTeamVolunteerSuccess,
  updateTeamVolunteerError
} from '../actions/teamActions';
import {
  getCoachesListError,
  getCoachesListSuccess,
  getCoachesTeamsSuccess,
  getCoachesTeamsError,
  addCoachToTeamSuccess,
  addCoachToTeamError,
  removeCoachFromTeamSuccess,
  removeCoachFromTeamError
} from '../actions/coachActions';
import {
  getMembersListError,
  getMembersListSuccess,
  getPlayersListError,
  getPlayersListSuccess,
  getMentorsListError,
  getMentorsListSuccess
} from '../actions/listActions';
import{
  updateMobileStateSuccess,
  updateMobileStateError
} from '../actions/mobileActions';
import {
  requestCheckJWT,
  requestUserUpdate,
  requestPasswordChange,
  requestGames,
  requestMyGames,
  requestUmpInfo,
  requestOrganizations,
  requestDivisions,
  requestSeasonDivisions,
  requestSeasons,
  requestAllSeasons,
  requestSeasonsByActive,
  requestSeasonsByDisplay,
  requestUpdateSeasonMaxDate,
  requestUpdateSeasonActive,
  requestUpdateSeasonDisplay,
  requestTeams,
  requestAssignUmpToGame,
  requestRemoveUmpFromGame,
  requestFilteredGames,
  requestCoachesList,
  requestMembersList,
  requestMentorsList,
  requestPlayersList,
  requestUmpiresList,
  requestUmpireUpdate,
  requestMenteeUpdate,
  requestMentorMenteeInfo,
  requestUmpiresNextGame,
  requestCoachesTeams,
  requestAddCoachToTeam,
  requestRemoveCoachFromTeam,
  requestTeamsVolunteering,
  requestTeamVolunteerUpdate,
  requestUpdateUserRole,
  requestDailySummary,
  requestSeasonUmpireData,
  requestUpdateUmpAvailableForGame,
  requestYtdDataSummary,
  requestWheelData,
  requestFullSeasonStats,
  requestUmpireStats,
  requestTeamVolunteerData,
  requestTodaysWeather,
  requestUpdateUserInfo,
  requestSendMessage,
} from './../actions/api';
import {
  getSeasonUmpireDataSuccess,
  getSeasonUmpireDataError,
  getYtdSummaryDataSuccess,
  getYtdSummaryDataError,
  getWheelDataSuccess,
  getWheelDataError,
  getFullSeasonStatsSuccess,
  getFullSeasonStatsError,
  getUmpireStatsSuccess,
  getUmpireStatsError,
  getTeamVolunteerDataSuccess,
  getTeamVolunteerDataError,
} from '../actions/reportsActions';
import {
  getTodaysWeatherError,
  getTodaysWeatherSuccess
} from '../actions/weatherActions';
import {
  sendMessageError,
  sendMessageSuccess
} from '../actions/smsActions';
import { clearErrors } from './../actions/errorActions';

//redux-saga selectors
const getToken = state => {
  return state.user.token;
};

/*const checkingToken = state => {
  return state.user.checkingToken;
};*/

export function* watchUserLogout() {
  while (true) {
    yield take(actions.USER_LOGOUT);
    yield fork(removeJWT);
  }
}

export function removeJWT() {
  window.localStorage.removeItem('kodaro-token');
}

export function* requestJWTCheck(token) {
  try {
    const response = yield call(requestCheckJWT, token);
    if (response.user) {
      yield put(userLoginSuccess(token, response.user, response.data));
    } else {
      yield put(userLogout());
    }
  } catch (e) {
    removeJWT();
  }
}

export function* watchCheckJWT() {
  while (true) {
    const { token } = yield take(actions.USER_CHECK_JWT);
    yield fork(requestJWTCheck, token);
  }
}

export function* requestProfileUpdate(name, phone) {
  try {
    const token = yield select(getToken);
    const response = yield call(requestUserUpdate, token, name, phone);
    if (response.status === 200) {
      yield put(updateUserProfileSuccess(name, phone));
    } else if (response.status === 401) {
      yield put(userLogout());
    } else {
      yield put(updateUserProfileError('Server error'));
    }
  } catch (e) {
    yield put(updateUserProfileError(e));
  }
}

export function* watchUserProfileUpdate() {
  while (true) {
    const { name, phone } = yield take(actions.USER_PROFILE_UPDATE);
    yield fork(requestProfileUpdate, name, phone);
  }
}

export function* userPasswordChange(password) {
  try {
    const token = yield select(getToken);
    const response = yield call(requestPasswordChange, token, password);
    if (response.status === 200) {
      yield put(updatePasswordSuccess());
    } else if (response.status === 401) {
      yield put(userLogout());
    } else {
      yield put(updatePasswordError('Server error'));
    }
  } catch (e) {
    yield put(updatePasswordError('Server error'));
  }
}

export function* watchUserPasswordChange() {
  while (true) {
    const { password } = yield take(actions.USER_PASSWORD_CHANGE);
    yield fork(userPasswordChange, password);
  }
}

export function* gamesRequest(startDate, numRows, lastStartDate, lastGameId) {
  try {
    let token = yield select(getToken);
    if (token === '') {
      const response = yield take(actions.USER_LOGIN_SUCCESS);
      token = response.token;
    }
    const { gamesList, games, error, status } = yield call(
      requestGames,
      token,
      numRows,
      lastStartDate,
      lastGameId,
      startDate,
      false
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getGamesError(error));
    } else {
      yield put(getGamesSuccess(gamesList, games));
    }
  } catch (error) {
    yield put(getGamesError(error));
  }
}

export function* watchGames() {
  while (true) {
    const { startDate, numRows, lastStartDate, lastGameId } = yield take(actions.GET_GAMES);
    yield fork(gamesRequest, startDate, numRows, lastStartDate, lastGameId);
  }
}

export function* updatedGamesRequest(startDate, numRows, lastStartDate, lastGameId) {
  try {
    let token = yield select(getToken);
    if (token === '') {
      const response = yield take(actions.USER_LOGIN_SUCCESS);
      token = response.token;
    }
    const { gamesList, games, error, status } = yield call(
      requestGames,
      token,
      numRows,
      lastStartDate,
      lastGameId,
      startDate,
      false
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getGamesError(error));
    } else {
      yield put(getUpdatedGamesSuccess(gamesList, games));
    }
  } catch (error) {
    yield put(getGamesError(error));
  }
}

export function* watchUpdatedGames() {
  while (true) {
    const { startDate, numRows, lastStartDate, lastGameId } = yield take(actions.UPDATED_GAMES);
    yield fork(updatedGamesRequest, startDate, numRows, lastStartDate, lastGameId);
  }
}

export function* gamesByDateRequest(startDate, numRows, lastStartDate, lastGameId) {
  try {
    const token = yield select(getToken);
    const { gamesList, games, error, status } = yield call(
      requestGames,
      token,
      numRows,
      lastStartDate,
      lastGameId,
      startDate,
      true
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getGamesByDateError(error));
    } else {
      yield put(getGamesByDateSuccess(startDate, gamesList, games));
    }
  } catch (error) {
    yield put(getGamesByDateError(error));
  }
}

export function* watchGamesByDate() {
  while (true) {
    const { startDate, numRows, lastStartDate, lastGameId } = yield take(actions.GET_GAMES_BY_DATE);
    yield fork(gamesByDateRequest, startDate, numRows, lastStartDate, lastGameId);
  }
}

export function* updatedMyGamesRequest(season, team, umpire, startDate) {
  try {
    const token = yield select(getToken);
    const { myGamesList, games, error, status } = yield call(requestMyGames, token, season, team, umpire, startDate);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getMyGamesError(error));
    } else {
      yield put(getUpdatedMyGamesSuccess(myGamesList, games));
    }
  } catch (error) {
    yield put(getMyGamesError(error));
  }
}

export function* watchUpdatedMyGames() {
  while (true) {
    const { season, team, umpire, startDate } = yield take(actions.UPDATED_MY_GAMES);
    yield fork(updatedMyGamesRequest, season, team, umpire, startDate);
  }
}

export function* myGamesRequest(season, team, umpire, startDate) {
  try {
    const token = yield select(getToken);
    const { myGamesList, games, error, status } = yield call(requestMyGames, token, season, team, umpire, startDate);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getMyGamesError(error));
    } else {
      yield put(getMyGamesSuccess(myGamesList, games));
    }
  } catch (error) {
    yield put(getMyGamesError(error));
  }
}

export function* watchMyGames() {
  while (true) {
    const { season, team, umpire, startDate } = yield take(actions.GET_MY_GAMES);
    yield fork(myGamesRequest, season, team, umpire, startDate);
  }
}

export function* filterGamesRequest(startDate, endDate, season, division, team, umpire, showAllGames, limitResults) {
  try {
    const token = yield select(getToken);
    const { filteredGamesList, games, error, status } = yield call(
      requestFilteredGames,
      token,
      startDate,
      endDate,
      season,
      division,
      team,
      umpire,
      showAllGames,
      limitResults
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(filterGamesError(error));
    } else {
      yield put(filterGamesSuccess(filteredGamesList, games));
    }
  } catch (error) {
    yield put(filterGamesError(error));
  }
}

export function* watchFilterGames() {
  while (true) {
    const { startDate, endDate, season, division, team, umpire, showAllGames, limitResults } = yield take(actions.FILTER_GAMES);
    yield fork(filterGamesRequest, startDate, endDate, season, division, team, umpire, showAllGames, limitResults);
  }
}

export function* umpInfo(memberId) {
  try {
    const token = yield select(getToken);
    const { umpires, umpiresList = [], error, status } = yield call(requestUmpInfo, token, memberId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getUmpInfoError(memberId, error));
    } else {
      yield put(getUmpInfoSuccess(umpires, umpiresList));
    }
  } catch (error) {
    yield put(getUmpInfoError(memberId, error));
  }
}

export function* watchUmpInfo() {
  while (true) {
    const { memberId } = yield take(actions.GET_UMP_INFO);
    yield fork(umpInfo, memberId);
  }
}

export function* dailySummary(summaryDay) {
  try {
    const token = yield select(getToken);
    const { dailySummary, error, status } = yield call(requestDailySummary, token, summaryDay);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getDailySummaryError(error));
    } else {
      yield put(getDailySummarySuccess(dailySummary));
    }
  } catch (error) {
    yield put(getDailySummaryError(error));
  }
}

export function* watchDailySummary() {
  while (true) {
    // yield take(actions.GET_DAILY_SUMMARY);
    // yield fork(dailySummary);
    const { summaryDay } = yield take(actions.GET_DAILY_SUMMARY);
    yield fork(dailySummary, summaryDay);
  }
}

//Organization Sagas

export function* organizationsGet() {
  try {
    const token = yield select(getToken);
    const { organizationsList, organizations, error, status } = yield call(requestOrganizations, token);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getOrganizationsError(error));
    } else {
      yield put(getOrganizationsSuccess(organizationsList, organizations));
    }
  } catch (error) {
    yield put(getOrganizationsError(error));
  }
}

export function* watchOrganizationsGet() {
  while (true) {
    yield take(actions.GET_ORGANIZATIONS);
    yield fork(organizationsGet);
  }
}

//Season Sagas

export function* seasonsGet() {
  try {
    const token = yield select(getToken);
    const { seasonsList, seasons, error, status } = yield call(requestSeasons, token);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonsError(error));
    } else {
      yield put(getSeasonsSuccess(seasonsList, seasons));
    }
  } catch (error) {
    yield put(getSeasonsError(error));
  }
}

export function* watchSeasonsGet() {
  while (true) {
    yield take(actions.GET_SEASONS);
    yield fork(seasonsGet);
  }
}

export function* seasonsGetAll() {
  try {
    const token = yield select(getToken);
    const { seasonsList, seasons, error, status } = yield call(requestAllSeasons, token);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonsError(error));
    } else {
      yield put(getAllSeasonsSuccess(seasonsList, seasons));
    }
  } catch (error) {
    yield put(getSeasonsError(error));
  }
}

export function* watchSeasonsGetAll() {
  while (true) {
    yield take(actions.GET_ALL_SEASONS);
    yield fork(seasonsGetAll);
  }
}

export function* seasonsGetByActive(activeSeason) {
  try {
    const token = yield select(getToken);
    const { seasonsList, seasons, error, status } = yield call(requestSeasonsByActive, token, activeSeason);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonsError(error));
    } else {
      yield put(getSeasonsSuccess(seasonsList, seasons));
    }
  } catch (error) {
    yield put(getSeasonsError(error));
  }
}

export function* seasonsGetByDisplay(displaySeason) {
  try {
    const token = yield select(getToken);
    const { seasonsList, seasons, error, status } = yield call(requestSeasonsByDisplay, token, displaySeason);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonsError(error));
    } else {
      yield put(getSeasonsSuccess(seasonsList, seasons));
    }
  } catch (error) {
    yield put(getSeasonsError(error));
  }
}

export function* watchSeasonsGetByActive() {
  while (true) {
    const { activeSeason } = yield take(actions.GET_SEASONS_BY_ACTIVE);
    yield fork(seasonsGetByActive, activeSeason);
  }
}

export function* watchSeasonsGetByDisplay() {
  while (true) {
    const { displaySeason } = yield take(actions.GET_SEASONS_BY_DISPLAY);
    yield fork(seasonsGetByDisplay, displaySeason);
  }
}

export function* updateSeasonMaxDate(seasonId, maxDate) {
  try {
    const token = yield select(getToken);
    const { error, status, newMaxDate } = yield call(requestUpdateSeasonMaxDate, token, seasonId, maxDate);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateSeasonMaxDateError(error));
    } else {
      yield put(updateSeasonMaxDateSuccess(seasonId, newMaxDate));
    }
  } catch (error) {
    yield put(updateSeasonMaxDateError(error));
  }
}

export function* updateSeasonActive(seasonId, active) {
  try {
    const token = yield select(getToken);
    const { error, status, newActive } = yield call(requestUpdateSeasonActive, token, seasonId, active);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateSeasonActiveError(error));
    } else {
      yield put(updateSeasonActiveSuccess(seasonId, newActive));
    }
  } catch (error) {
    yield put(updateSeasonActiveError(error));
    yield put('An unexpected error occurred'); // Dispatch an action to show error notification
  }
}

export function* updateSeasonDisplay(seasonId, display) {
  try {
    const token = yield select(getToken);
    const { error, status, newDisplay } = yield call(requestUpdateSeasonDisplay, token, seasonId, display);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateSeasonDisplayError(error));
    } else {
      yield put(updateSeasonDisplaySuccess(seasonId, newDisplay));
    }
  } catch (error) {
    yield put(updateSeasonDisplayError(error));
    yield put('An unexpected error occurred'); // Dispatch an action to show error notification
  }
}

export function* watchUpdateSeasonMaxDate() {
  while (true) {
    const { seasonId, maxDate } = yield take(actions.UPDATE_SEASON_MAX_DATE);
    yield fork(updateSeasonMaxDate, seasonId, maxDate);
  }
}

export function* watchUpdateSeasonActive() {
  while (true) {
    const { seasonId, active } = yield take(actions.UPDATE_SEASON_ACTIVE);
    yield fork(updateSeasonActive, seasonId, active);
  }
}

export function* watchUpdateSeasonDisplay() {
  while (true) {
    const { seasonId, display } = yield take(actions.UPDATE_SEASON_DISPLAY);
    yield fork(updateSeasonDisplay, seasonId, display);
  }
}

//Divisions Sagas

export function* watchDivisionsGet() {
  while (true) {
    yield take(actions.GET_DIVISIONS);
    yield fork(divisionsRequest);
  }
}

export function* divisionsRequest() {
  try {
    const token = yield select(getToken);
    const { divisionsList, parentDivisionsList, divisions, error, status } = yield call(requestDivisions, token);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getDivisionsError(error));
    } else {
      yield put(getDivisionsSuccess(divisionsList, parentDivisionsList, divisions));
    }
  } catch (error) {
    yield put(getDivisionsError(error));
  }
}

export function* seasonDivisions(seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, divisionsList, divisions } = yield requestSeasonDivisions(token, seasonId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonDivisionsError(error));
    } else {
      yield put(getSeasonDivisionsSuccess(divisionsList, divisions));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchGetSeasonDivisions() {
  while (true) {
    const { seasonId } = yield take(actions.GET_SEASON_DIVISIONS);
    yield fork(seasonDivisions, seasonId);
  }
}

export function* watchTeamsGet() {
  while (true) {
    const { seasonId, divisionId } = yield take(actions.GET_TEAMS);
    yield fork(teamsRequest, divisionId, seasonId);
  }
}

export function* teamsRequest(divisionId, seasonId) {
  try {
    const token = yield select(getToken);
    const { teamsList, teams, error, status } = yield call(requestTeams, token, seasonId, divisionId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getTeamsError(error));
    } else {
      yield put(getTeamsSuccess(teamsList, teams));
    }
  } catch (error) {
    yield put(getTeamsError(error));
  }
}

export function* watchAddUmpToGame() {
  while (true) {
    const { umpireId, gameId, plate } = yield take(actions.ASSIGN_UMP_TO_GAME);
    yield fork(assignUmpToGameRequest, umpireId, gameId, plate);
  }
}

export function* assignUmpToGameRequest(umpireId, gameId, plate) {
  try {
    const token = yield select(getToken);
    const { updatedGame, error, status } = yield requestAssignUmpToGame(token, umpireId, gameId, plate);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(assignUmpToGameError(umpireId, gameId, plate, error, updatedGame));
    } else {
      yield put(assignUmpToGameSuccess(umpireId, gameId, updatedGame));
    }
  } catch (e) {
    yield put(assignUmpToGameError(umpireId, gameId, plate, e));
  }
}

export function* watchRemoveUmpFromGame() {
  while (true) {
    const { umpireId, gameId, plate } = yield take(actions.REMOVE_UMP_FROM_GAME);
    yield fork(removeUmpFromGameRequest, umpireId, gameId, plate);
  }
}

export function* removeUmpFromGameRequest(umpireId, gameId, plate) {
  try {
    const token = yield select(getToken);
    const { updatedGame, error, status } = yield requestRemoveUmpFromGame(token, umpireId, gameId, plate);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(removeUmpError(umpireId, gameId, plate, error, updatedGame));
    } else {
      yield put(removeUmpSuccess(umpireId, gameId, updatedGame));
    }
  } catch (e) {
    yield put(removeUmpError(umpireId, gameId, plate, e));
  }
}

export function* watchCoachesList() {
  while (true) {
    const { seasonId } = yield take(actions.GET_COACHES_LIST);
    try {
      const token = yield select(getToken);
      const { coachesList, error, status } = yield requestCoachesList(token, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getCoachesListError(error));
      } else {
        yield put(getCoachesListSuccess(coachesList));
      }
    } catch (e) {
      yield put(getCoachesListError(e));
    }
  }
}

export function* watchPlayersList() {
  while (true) {
    const { seasonId } = yield take(actions.GET_PLAYERS_LIST);
    try {
      const token = yield select(getToken);
      const { playersList, error, status } = yield requestPlayersList(token, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getPlayersListError(error));
      } else {
        yield put(getPlayersListSuccess(playersList));
      }
    } catch (e) {
      yield put(getPlayersListError(e));
    }
  }
}

export function* watchMembersList() {
  while (true) {
    yield take(actions.GET_MEMBERS_LIST);
    try {
      const token = yield select(getToken);
      const { membersList, error, status } = yield requestMembersList(token);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getMembersListError(error));
      } else {
        yield put(getMembersListSuccess(membersList));
      }
    } catch (e) {
      yield put(getMembersListError(e));
    }
  }
}

export function* watchMentorsList() {
  while (true) {
    const { seasonId } = yield take(actions.GET_MENTORS_LIST);
    try {
      const token = yield select(getToken);
      const { mentorsList, error, status } = yield requestMentorsList(token, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getMentorsListError(error, seasonId));
      } else {
        yield put(getMentorsListSuccess(mentorsList, seasonId));
      }
    } catch (e) {
      yield put(getMentorsListError(e, seasonId));
    }
  }
}

export function* watchUmpiresList() {
  while (true) {
    const { seasonId } = yield take(actions.GET_UMPIRES_LIST);
    try {
      const token = yield select(getToken);
      const { umpiresList, umpires, error, status } = yield requestUmpiresList(token, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getUmpiresListError(seasonId, error));
      } else {
        yield put(getUmpiresListSuccess(seasonId, umpiresList, umpires));
      }
    } catch (e) {
      yield put(getUmpiresListError(e));
    }
  }
}

export function* umpireUpdate(memberId, umpireLevel, plateUmpire, baseUmpire, isMentor, isActive, isYouth, seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, updatedUmp } = yield requestUmpireUpdate(
      token,
      memberId,
      umpireLevel,
      plateUmpire, 
      baseUmpire,
      isMentor,
      isActive,
      isYouth,
      seasonId
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateUmpError(memberId, seasonId, error));
    } else {
      yield put(updateUmpSuccess(memberId, seasonId, updatedUmp));
    }
  } catch (error) {
    yield put(updateUmpError(memberId, error));
  }
}

export function* watchUmpUpdate() {
  while (true) {
    const { memberId, umpireLevel, plateUmpire, baseUmpire, isMentor, isActive, isYouth, seasonId } = yield take(actions.UPDATE_UMP);
    yield fork(umpireUpdate, memberId, umpireLevel, plateUmpire, baseUmpire, isMentor, isActive, isYouth, seasonId);
  }
}

export function* updateMentees(mentorId, menteeId, column, seasonId, operation) {
  try {
    const token = yield select(getToken);
    const { error, status, updatedMentees } = yield requestMenteeUpdate(
      token,
      mentorId,
      menteeId,
      column,
      seasonId,
      operation
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateMentorsMenteesError(mentorId, error));
    } else {
      yield put(updateMentorsMenteesSuccess(updatedMentees));
    }
  } catch (error) {
    yield put(updateMentorsMenteesError(mentorId, error));
  }
}

export function* watchUpdateMentees() {
  while (true) {
    const { mentorId, menteeId, column, seasonId, operation } = yield take(actions.UPDATE_MENTEES);
    yield fork(updateMentees, mentorId, menteeId, column, seasonId, operation);
  }
}

export function* watchMentorMenteeInfo() {
  while (true) {
    try {
      const { memberId, seasonId } = yield take(actions.GET_MENTOR_MENTEE_INFO);
      const token = yield select(getToken);
      const { error, status, mentorMenteeInfo } = yield requestMentorMenteeInfo(token, memberId, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getMentorMenteeInfoError(error));
      } else {
        yield put(getMentorMenteeInfoSuccess(mentorMenteeInfo));
      }
    } catch (error) {
      yield put(getMentorMenteeInfoError(error));
    }
  }
}

export function* umpsNextGame(memberId, umpType) {
  try {
    const token = yield select(getToken);
    const { error, status, nextGame } = yield requestUmpiresNextGame(token, memberId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getUmpsNextGameError(memberId, error, umpType));
    } else {
      yield put(getUmpsNextGameSuccess(memberId, nextGame, umpType));
    }
  } catch (error) {
    yield put(getUmpsNextGameError(memberId, error, umpType));
  }
}

export function* watchUmpNextGame() {
  while (true) {
    const { memberId, umpType } = yield take(actions.GET_UMPS_NEXT_GAME);
    yield fork(umpsNextGame, memberId, umpType);
  }
}

export function* sendClearErrors() {
  try {
    yield clearErrors();
  } catch (error) {
    console.log(error);
  }
}

export function* watchClearErrors() {
  while (true) {
    yield take(actions.CLEAR_ERRORS);
    yield fork(sendClearErrors);
  }
}

export function* watchCoachesTeams() {
  while (true) {
    try {
      const { memberId, seasonId } = yield take(actions.GET_COACHES_TEAMS);
      const token = yield select(getToken);
      const { error, status, teamsList } = yield requestCoachesTeams(token, memberId, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getCoachesTeamsError(error));
      } else {
        yield put(getCoachesTeamsSuccess(teamsList));
      }
    } catch (error) {
      yield put(getCoachesTeamsError(error));
    }
  }
}

export function* watchAddCoachToTeam() {
  while (true) {
    try {
      const { seasonId, teamId, teamName, divisionName } = yield take(actions.ADD_COACH_TO_TEAM);
      const token = yield select(getToken);
      const { error, status } = yield requestAddCoachToTeam(token, teamId, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(addCoachToTeamError(error));
      } else {
        yield put(addCoachToTeamSuccess(teamId, teamName, divisionName));
      }
    } catch (error) {
      yield put(addCoachToTeamError(error));
    }
  }
}

export function* watchRemoveCoachFromTeam() {
  while (true) {
    const { memberId, teamId, seasonId } = yield take(actions.REMOVE_COACH_FROM_TEAM);
    yield fork(removeCoachFromTeam, memberId, teamId, seasonId);
  }
}

export function* removeCoachFromTeam(memberId, teamId, seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status } = yield requestRemoveCoachFromTeam(token, memberId, teamId, seasonId);
    if (status === 401) {
      yield put(userLogout);
    } else if (error) {
      yield put(removeCoachFromTeamError(error));
    } else {
      yield put(removeCoachFromTeamSuccess(memberId, teamId, seasonId));
    }
  } catch (e) {
    yield put(removeCoachFromTeamError(e));
  }
}

export function* watchTeamsVolunteering() {
  while (true) {
    try {
      const { memberId, seasonId } = yield take(actions.GET_TEAMS_VOLUNTEERING);
      const token = yield select(getToken);
      const { error, status, teamsList } = yield requestTeamsVolunteering(token, memberId, seasonId);
      if (status === 401) {
        yield put(userLogout());
      } else if (error) {
        yield put(getTeamsVolunteeringError(error));
      } else {
        yield put(getTeamsVolunteeringSuccess(teamsList));
      }
    } catch (error) {
      yield put(getTeamsVolunteeringError(error));
    }
  }
}

export function* teamVolunteerUpdate({ memberId, seasonId, teamId, column, value, operation }) {
  try {
    const token = yield select(getToken);
    const { error, status, teamsList } = yield requestTeamVolunteerUpdate(
      token,
      memberId,
      seasonId,
      teamId,
      column,
      value,
      operation
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateTeamVolunteerError(error));
    } else {
      yield put(updateTeamVolunteerSuccess(teamsList));
    }
  } catch (error) {
    yield put(updateTeamVolunteerError(error));
  }
}

export function* watchTeamVolunteerUpdate() {
  yield takeLatest(actions.UPDATE_TEAM_VOLUNTEER, teamVolunteerUpdate);
}

export function* updateUserInfo(memberId, email, firstName, lastName, cellPhone) {
  try {
    const token = yield select(getToken);
    const response = yield requestUpdateUserInfo(token, memberId, email, firstName, lastName, cellPhone);
    const { status, error, user} = response;
    if (status === 200) {
      yield put(userInfoUpdateSuccess(user));
    } else {
      yield put(userInfoUpdateError(error));
    }
  } catch (error) {
     yield put(userInfoUpdateError(error));
  }
}

export function* watchUpdateUserInfo() {
  while (true) {
    const { memberId, email, firstName, lastName, cellPhone } = yield take(actions.UPDATE_USER_INFO);
    yield fork(updateUserInfo, memberId, email, firstName, lastName, cellPhone);
  }
}

export function* updateUserRole(memberId, email, isAdmin) {
  try {
    const token = yield select(getToken);
    const { error, status, user } = yield requestUpdateUserRole(token, memberId, email, isAdmin);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(userRoleUpdateError(error));
    } else {
      yield put(userRoleUpdateSuccess(user));
    }
  } catch (error) {
    yield put(userRoleUpdateError(error));
  }
}

export function* watchUpdateUserRole() {
  while (true) {
    const { memberId, email, isAdmin } = yield take(actions.UPDATE_USER_ROLE);
    yield fork(updateUserRole, memberId, email, isAdmin);
  }
}

export function* setUpdateMobileState(mobile) {
  try {
    if (mobile){
      yield put(updateMobileStateSuccess(mobile));
    } else {
      yield put(updateMobileStateError("No value passed in"));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchUpdateMobileState() {
  while (true) {
    const { mobile } = yield take(actions.UPDATE_MOBILE_STATE);
    yield fork(setUpdateMobileState, mobile);
  }
}

export function* setUserSeason(userSeason) {
  try {
      yield put(changeUserSeasonSuccess(userSeason));

  } catch (error) {
    console.log(error);
  }
}

export function* watchSetUserSeason() {
  while (true) {
    const { userSeason } = yield take(actions.CHANGE_USER_SEASON);
    yield fork(setUserSeason, userSeason);
  }
}

export function* seasonUmpireData(seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, umpires, umpiresList } = yield requestSeasonUmpireData(token, seasonId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getSeasonUmpireDataError(error));
    } else {
      yield put(getSeasonUmpireDataSuccess(umpires, umpiresList));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchGetSeasonUmpireData() {
  while (true) {
    const { seasonId } = yield take(actions.GET_SEASON_UMPIRE_DATA);
    yield fork(seasonUmpireData, seasonId);
  }
}

export function* ytdSummaryData() {
  try {
    const token = yield select(getToken);
    const { error, status, seasons, seasonsList } = yield requestYtdDataSummary(token);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getYtdSummaryDataError(error));
    } else {
      yield put(getYtdSummaryDataSuccess(seasons, seasonsList));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchYtdSummaryData() {
  while (true) {
    yield take(actions.GET_YTD_DATA);
    yield fork(ytdSummaryData);
  }
}


export function* getWheelData(seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, wheelUmpires, wheelUmpiresList } = yield requestWheelData(token, seasonId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getWheelDataError(error));
    } else {
      yield put(getWheelDataSuccess(wheelUmpires, wheelUmpiresList));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchGetWheelData() {
  while (true) {
    const { seasonId } = yield take(actions.GET_WHEEL_DATA);
    yield fork(getWheelData, seasonId);
  }
}

export function* getFullSeasonStats(parentSeasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, seasons, seasonsList, umpires, umpiresList } = yield requestFullSeasonStats(token, parentSeasonId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getFullSeasonStatsError(error));
    } else {
      yield put(getFullSeasonStatsSuccess(seasons, seasonsList, umpires, umpiresList));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchFullSeasonStats() {
  while (true) {
    const { parentSeasonId } = yield take(actions.GET_FULL_SEASON_STATS);
    yield fork(getFullSeasonStats, parentSeasonId);
  }
}

export function* getUmpireStats(seasonId, memberId) {
  try {
    const token = yield select(getToken);
    const { error, status, umpireStats } = yield requestUmpireStats(token, seasonId, memberId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getUmpireStatsError(error));
    } else {
      yield put(getUmpireStatsSuccess(umpireStats));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchGetUmpireStats() {
  while (true) {
    const { seasonId, memberId } = yield take(actions.GET_UMPIRE_STATS);
    yield fork(getUmpireStats, seasonId, memberId);
  }
}

export function* teamVolunteerData(seasonId) {
  try {
    const token = yield select(getToken);
    const { error, status, teams, teamsList } = yield requestTeamVolunteerData(token, seasonId);
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(getTeamVolunteerDataError(error));
    } else {
      yield put(getTeamVolunteerDataSuccess(teams, teamsList));
    }
  } catch (error) {
    console.log(error);
  }
}

export function* watchGetTeamVolunteerData() {
  while (true) {
    const { seasonId } = yield take(actions.GET_TEAM_VOLUNTEER_DATA);
    yield fork(teamVolunteerData, seasonId);
  }
}

export function* setUmpAvailableForGame(gameID, umpireAvailable) {
  try {
    const token = yield select(getToken);
    const { error, status, gameId, umpAvailable } = yield requestUpdateUmpAvailableForGame(
      token,
      gameID,
      umpireAvailable
    );
    if (status === 401) {
      yield put(userLogout());
    } else if (error) {
      yield put(updateUmpAvailableForGameError(error));
    } else {
      yield put(updateUmpAvailableForGameSuccess(gameId, umpAvailable));
    }
  } catch (error) {
    yield put(updateUmpAvailableForGameError(error));
  }
}

export function* watchUpdateUmpAvailableForGame() {
  while (true) {
    const { gameId, umpAvailable } = yield take(actions.UPDATE_UMP_AVAILABLE_FOR_GAME);
    yield fork(setUmpAvailableForGame, gameId, umpAvailable);
  }
}

export function* getTodaysWeather(latitude, longitude, timezone){
  try{
    const token = yield select(getToken);
    const { error, status, todaysWeather } = yield requestTodaysWeather(token, latitude, longitude, timezone);
    if(status === 401) {
      yield put(userLogout());
    } else if(error){
      yield put(getTodaysWeatherError(error));
    } else {
      yield put(getTodaysWeatherSuccess(todaysWeather));
    }
  } catch (error){
    yield put(getTodaysWeatherError(error));
  }
}

export function* watchGetTodaysWeather(){
  while(true){
    const { latitude, longitude, timezone } = yield take(actions.GET_TODAYS_WEATHER);
    yield fork(getTodaysWeather, latitude, longitude, timezone);
  }
}

export function* sendMessage(message, recipients){
  try{
    const token = yield select(getToken);
    const { error, status, response } = yield requestSendMessage(token, message, recipients);
    if(status === 401) {
      yield put(userLogout());
    } else if(error){
      yield put(sendMessageError(error));
    } else {
      yield put(sendMessageSuccess(response));
    }
  } catch (error){
    yield put(sendMessageError(error));
  }
}

export function* watchSendMessage(){
  while(true){
    const { message, recipients } = yield take(actions.SEND_MESSAGE);
    yield fork(sendMessage, message, recipients);
  }
}

export default function* rootSaga() {
  yield all([
    fork(watchUserLogout),
    fork(watchCheckJWT),
    fork(watchUserProfileUpdate),
    fork(watchUserPasswordChange),
    fork(watchGames),
    fork(watchMyGames),
    fork(watchGamesByDate),
    fork(watchFilterGames),
    fork(watchUmpInfo),
    fork(watchAddUmpToGame),
    fork(watchRemoveUmpFromGame),
    fork(watchOrganizationsGet),
    fork(watchSeasonsGet),
    fork(watchSeasonsGetAll),
    fork(watchSeasonsGetByActive),
    fork(watchSeasonsGetByDisplay),
    fork(watchUpdateSeasonMaxDate),
    fork(watchUpdateSeasonActive),
    fork(watchUpdateSeasonDisplay),
    fork(watchDivisionsGet),
    fork(watchGetSeasonDivisions),
    fork(watchTeamsGet),
    fork(watchCoachesList),
    fork(watchPlayersList),
    fork(watchMembersList),
    fork(watchMentorsList),
    fork(watchUmpiresList),
    fork(watchUmpUpdate),
    fork(watchUpdateMentees),
    fork(watchMentorMenteeInfo),
    fork(watchUmpNextGame),
    fork(watchClearErrors),
    fork(watchCoachesTeams),
    fork(watchAddCoachToTeam),
    fork(watchRemoveCoachFromTeam),
    fork(watchTeamsVolunteering),
    fork(watchTeamVolunteerUpdate),
    fork(watchUpdatedMyGames),
    fork(watchUpdatedGames),
    fork(watchUpdateUserRole),
    fork(watchDailySummary),
    fork(watchUpdateMobileState),
    fork(watchSetUserSeason),
    fork(watchGetSeasonUmpireData),
    fork(watchYtdSummaryData),
    fork(watchGetWheelData),
    fork(watchFullSeasonStats),
    fork(watchGetUmpireStats),
    fork(watchGetTeamVolunteerData),
    fork(watchUpdateUmpAvailableForGame),
    fork(watchGetTodaysWeather),
    fork(watchUpdateUserInfo),
    fork(watchSendMessage),
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/index.js